import { useCallback, useState } from 'react';
import data_init_new from './data_init_new.json';
import PermissionService from '../../../../services/Permission.service';
import { useNavigate } from 'react-router-dom';
import useNotify from '../../../../context/Notification/useNotify';
import { useTranslation } from 'react-i18next';
import Icon from './icon';
import UserService from '../../../../services/UserService';
import { MENU_KEY, SUBMENU_KEY } from '../../../landing/json_data';

export const PACKAGE = {
    COOKIE: 1,
    STARTER: 2,
    PROFESSIONAL: 3,
    ENTERPRISE: 4,
};

export const NOT_ACCESS_MENU_BY_PACKAGE = {
    [PACKAGE.COOKIE]: [
        {
            mainMenuName: MENU_KEY.DATA_CATALOG,
        },
        {
            mainMenuName: MENU_KEY.DATA_INVENTORY,
        },
        {
            mainMenuName: MENU_KEY.RISK_MANAGEMENT,
        },
        {
            mainMenuName: MENU_KEY.DPIA,
        },
        {
            mainMenuName: MENU_KEY.DATA_SUBJECT_MANAGEMENT,
        },
        {
            mainMenuName: MENU_KEY.PRIVACY_POLICY,
        },
        {
            mainMenuName: MENU_KEY.CONSENT_MANAGEMENT,
        },
        {
            mainMenuName: MENU_KEY.DATA_BREACH,
        },
        {
            mainMenuName: MENU_KEY.DATA_INQUIRY,
        },
        {
            subMenuList: [
                SUBMENU_KEY.CONFIGURATION.DC_DPO,
                SUBMENU_KEY.CONFIGURATION.DATA_CATALOG,
                SUBMENU_KEY.CONFIGURATION.EMAIL_SETTING,
            ],
        },
    ],
    [PACKAGE.STARTER]: [
        {
            mainMenuName: MENU_KEY.DATA_CATALOG,
        },
        {
            mainMenuName: MENU_KEY.DATA_SUBJECT_MANAGEMENT,
        },
        {
            mainMenuName: MENU_KEY.DATA_BREACH,
        },
    ],
    [PACKAGE.PROFESSIONAL]: [
        {
            mainMenuName: MENU_KEY.DATA_CATALOG,
        },
    ],
    [PACKAGE.ENTERPRISE]: [],
};

const checkSubMenuPermissions = (item, notAccessMenu) => {
    if (item.subMenuList) {
        item.subMenuList.forEach((subMenu) => {
            if (
                notAccessMenu.some((menu) =>
                    menu.subMenuList?.includes(subMenu.subMenuName)
                )
            ) {
                subMenu.packagePermission = false;
            }
        });
    }
};

// Temporary when user package is 1 sub menu in configuration set packagePermission to false such as SUBMENU_KEY.CONFIGURATION.DC_DPO, SUBMENU_KEY.CONFIGURATION.DATA_CATALOG, SUBMENU_KEY.CONFIGURATION.EMAIL_SETTING,
export const setPackagePermission = (menuAccessPermissions) => {
    const currentPackage = UserService.currentPackage();
    const notAccessMenu = NOT_ACCESS_MENU_BY_PACKAGE[currentPackage];
    if (notAccessMenu) {
        menuAccessPermissions.forEach((item) => {
            if (
                notAccessMenu.some(
                    (menu) => menu.mainMenuName === item.mainMenuName
                )
            ) {
                item.packagePermission = false;
            }
            checkSubMenuPermissions(item, notAccessMenu);
        });
    }
    return menuAccessPermissions;
};

export default function usePermission() {
    const navigate = useNavigate();
    const notify = useNotify();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [permissionList, setPermissionList] = useState([]);
    const [permissionDetail, setPermissionDetail] = useState();

    const getPermissionList = useCallback(async () => {
        setLoading(true);

        const reset = () => {
            setLoading(false);
            setPermissionList([]);
        };

        try {
            const { data } = await PermissionService.getPermissionList();
            if (data?.resultData) {
                setPermissionList(data.resultData.result);

                setLoading(false);
            } else {
                reset();
            }
        } catch (error) {
            reset();
            console.log(error);
        }
    }, []);

    const getPermissionById = useCallback(async (id) => {
        setLoading(true);

        const reset = () => {
            setLoading(false);
            setPermissionDetail();
        };

        try {
            const { data } = await PermissionService.getPermissionById(id);
            if (data?.resultData) {
                setPermissionDetail(data.resultData);
                setLoading(false);
            } else {
                reset();
            }
        } catch (error) {
            reset();
            console.log(error);
        }
    }, []);

    const createdPermission = useCallback(
        async ({ permissionName, sequence }, action) => {
            setLoading(true);

            const reset = () => {
                setLoading(false);
                notify({
                    type: 'error',
                    title: t('common.Fail'),
                    description: t('Failed to submit data'),
                });
            };

            // Function to get the data_init_new and check the package set packagePermission in data_init_new to false when have item in NOT_ACCESS_MENU_BY_PACKAGE
            const data_init_temp = data_init_new;
            const payload = {
                permissionName,
                permissionDetail: JSON.stringify(
                    setPackagePermission(data_init_temp)
                ),
                sequence: sequence,
            };

            try {
                const { data } = await PermissionService.createdPermission(
                    payload
                );
                if (data?.resultData) {
                    setLoading(false);
                    action(data?.resultData?.organizePermissionId);
                    navigate(`?id=${data?.resultData?.organizePermissionId}`);
                    notify({
                        title: t('common.Success'),
                        description: t('Successfully save data'),
                        timeOut: 1800,
                    });
                } else {
                    reset();
                }
            } catch (error) {
                reset();
                console.log(error);
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [navigate, notify, t]
    );

    const updatePermission = useCallback(
        async (payload, action) => {
            setLoading(true);

            const reset = () => {
                setLoading(false);
                notify({
                    type: 'error',
                    title: t('common.Fail'),
                    description: t('Failed to submit data'),
                });
            };

            try {
                const { data } = await PermissionService.updatePermission(
                    payload
                );
                if (data?.resultCode === 1) {
                    setLoading(false);
                    action();
                    notify({
                        title: t('common.Success'),
                        description: t('Successfully save data'),
                        timeOut: 1800,
                    });
                } else {
                    reset();
                }
            } catch (error) {
                reset();
                console.log(error);
            }
        },
        [notify, t]
    );

    const deletePermission = useCallback(async (id, action) => {
        setLoading(true);

        try {
            const { data } = await PermissionService.deletePermission(id);
            if (data?.resultCode === 1) {
                setLoading(false);
                action();
                navigate('');
            } else {
                setLoading(false);
                notify({
                    type: 'error',
                    title: t('common.Fail'),
                    description: t('Failed to deleted'),
                });
            }
        } catch (error) {
            if (error?.data?.errorData.code === 500) {
                notify({
                    type: 'error',
                    icon: <Icon />,
                    title: t('Permission cannot be removed'),
                    closeable: true,
                    description: t(
                        'Because this permission is already in use for the user'
                    ),
                });
                action();
            }
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        loading,
        permissionList,
        permissionDetail,
        getPermissionById,
        getPermissionList,
        createdPermission,
        deletePermission,
        updatePermission,
    };
}
